import { useParams } from "react-router-dom";

import { defaultAgentGroupName, defaultEnvironmentName } from "shared/model/AgentGroup";

interface WorkspaceRouteParams extends Record<string, string | undefined> {
    workspaceName?: string;
    environmentName?: string;
    agentGroupName?: string;
}

interface WorkspaceRouteParamOverrides {
    workspaceName?: string;
}

interface WorkspaceRouteParamOverridesWithOptionalNames extends WorkspaceRouteParamOverrides {
    environmentName?: string | null;
    agentGroupName?: string | null;
}

export interface WorkspaceRouteState {
    workspaceName?: string;
    environmentName: string;
    agentGroupName: string;
    getPathWithWorkspace: (basePath: string, suffix?: string) => string;
}

export interface WorkspaceRouteStateWithOptionalNames extends Omit<WorkspaceRouteState, "environmentName" | "agentGroupName"> {
    environmentName: string | undefined;
    agentGroupName: string | undefined;
}

const resolveSegment = (paramValue: string | undefined, override: string | null | undefined, defaultValue: string): string | undefined => {
    if (paramValue !== undefined) {
        return paramValue;
    }
    if (override === null) {
        return undefined;
    }
    if (override !== undefined) {
        return override;
    }
    return defaultValue;
};

/**
 * Decomposes {workspaceName, environmentName, agentGroupName} out of the current route match.
 * `overrides` supplies a fallback when a segment is missing from the URL, in place of this hook's
 * own built-ins (none for workspaceName; "Default" for environmentName/agentGroupName) - pass
 * `null` for environmentName/agentGroupName to opt out of defaulting entirely, which widens the
 * return type to reflect the possible `undefined`.
 */
function useWorkspaceRouteParams(overrides?: WorkspaceRouteParamOverrides): WorkspaceRouteState;
function useWorkspaceRouteParams(overrides: WorkspaceRouteParamOverridesWithOptionalNames): WorkspaceRouteStateWithOptionalNames;
function useWorkspaceRouteParams(
    overrides?: WorkspaceRouteParamOverridesWithOptionalNames
): WorkspaceRouteState | WorkspaceRouteStateWithOptionalNames {
    const { workspaceName, environmentName, agentGroupName } = useParams<WorkspaceRouteParams>();

    const resolvedWorkspaceName = workspaceName ?? overrides?.workspaceName;
    const resolvedEnvironmentName = resolveSegment(environmentName, overrides?.environmentName, defaultEnvironmentName);
    const resolvedAgentGroupName = resolveSegment(agentGroupName, overrides?.agentGroupName, defaultAgentGroupName);

    const getPathWithWorkspace = (basePath: string, suffix = "") =>
        `${basePath}/${resolvedWorkspaceName}/${resolvedEnvironmentName}/${resolvedAgentGroupName}${suffix}`;

    return {
        workspaceName: resolvedWorkspaceName,
        environmentName: resolvedEnvironmentName,
        agentGroupName: resolvedAgentGroupName,
        getPathWithWorkspace,
    };
}

export default useWorkspaceRouteParams;
